import { t } from "@lingui/macro";
import {
  ArrowRightIcon,
  CircleNotchIcon,
  FilePdfIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef, useState } from "react";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

import { RateLimitWaitlistForm } from "../_components/rate-limit-waitlist-form";
import { ToolShareButtons } from "../_components/tool-share-buttons";
import { buildPublicToolUrl, getApiErrorMessage, recordToolCtaClick } from "../_utils";

const maxPdfUploadSize = 5 * 1024 * 1024;
const encouragementMarker = "[ENCOURAGEMENT]";
const toolPath = "/tools/resume-roast";

type ParsedEvent = {
  event: string;
  data: string;
};

const parseSseEvent = (rawEvent: string): ParsedEvent => {
  const lines = rawEvent.split(/\r?\n/);
  let event = "message";
  const data: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
      continue;
    }

    if (line.startsWith("data:")) {
      const value = line.slice(5);
      data.push(value.startsWith(" ") ? value.slice(1) : value);
    }
  }

  return { event, data: data.join("\n") };
};

const splitRoastText = (value: string) => {
  const [roast, ...encouragementParts] = value.split(encouragementMarker);
  return {
    roast: roast.trim(),
    encouragement: encouragementParts.join(encouragementMarker).trim(),
  };
};

export const ResumeRoastPage = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [roastText, setRoastText] = useState("");
  const [error, setError] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState("");

  const toolUrl = buildPublicToolUrl(toolPath);
  const shareMessage = t`I just got my resume roasted on Apply Edge. Get yours free: ${toolUrl}`;
  const splitContent = splitRoastText(roastText);

  const resetForNextRun = () => {
    setError("");
    setRateLimitMessage("");
    setRoastText("");
    setIsComplete(false);
  };

  const onFileSelected = (file: File | null) => {
    resetForNextRun();

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (file.type !== "application/pdf") {
      setSelectedFile(null);
      setError(t`File must be a PDF and under 5 MB.`);
      return;
    }

    if (file.size > maxPdfUploadSize) {
      setSelectedFile(null);
      setError(t`File must be a PDF and under 5 MB.`);
      return;
    }

    setSelectedFile(file);
  };

  const onSubmit = async () => {
    if (!selectedFile) {
      setError(t`Choose a PDF first.`);
      return;
    }

    setIsStreaming(true);
    setError("");
    setRateLimitMessage("");
    setRoastText("");
    setIsComplete(false);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await fetch("/api/tools/resume-roast", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message = getApiErrorMessage(payload, t`Something went wrong. Please try again.`);

        if (response.status === 429) {
          setRateLimitMessage(message);
        } else {
          setError(message);
        }

        return;
      }

      if (!response.body) {
        setError(t`The stream ended before any roast was received.`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });

        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() ?? "";

        for (const rawChunk of chunks) {
          if (!rawChunk.trim()) {
            continue;
          }

          const parsed = parseSseEvent(rawChunk);

          if (parsed.event === "done") {
            streamDone = true;
            break;
          }

          if (parsed.event === "error") {
            let payload: unknown = {};

            try {
              payload = JSON.parse(parsed.data);
            } catch {
              payload = {};
            }

            throw new Error(getApiErrorMessage(payload, t`AI service error — please try again.`));
          }

          if (parsed.data) {
            setRoastText((current) => current + parsed.data);
          }
        }

        if (done) {
          streamDone = true;
        }
      }

      setIsComplete(true);
    } catch (streamError) {
      setError(
        streamError instanceof Error
          ? streamError.message
          : t`Something went wrong while streaming the roast.`,
      );
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 pb-24 pt-32 delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both sm:px-6 sm:pt-36">
      <SEO
        title={t`Free AI Resume Roast | Apply Edge`}
        description={t`Get brutally honest AI feedback on your resume. Upload your resume and get actionable suggestions to land more interviews.`}
        canonical="/tools/resume-roast"
      />
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <div className="max-w-3xl space-y-4">
          <Badge className="w-fit border border-fuchsia-400/30 bg-fuchsia-400/10 text-xs font-medium text-fuchsia-700 dark:text-fuchsia-300">
            {t`Free Tool`}
          </Badge>
          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
            {t`Upload your resume and let the AI roast it like it owes you honesty.`}
          </h1>
          <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
            {t`Drop in a PDF resume and get a sharp, funny review that calls out weak bullets, vague claims, and missing metrics, followed by one real note of encouragement.`}
          </p>
        </div>

        {rateLimitMessage ? (
          <Card className="flex-col border-amber-400/30 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
            <CardHeader className="space-y-2">
              <CardTitle className="flex items-center gap-2 text-2xl text-foreground">
                <WarningCircleIcon className="text-warning-strong" size={22} />
                {t`Daily roast limit reached`}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {rateLimitMessage}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t`Leave your email and we'll let you know as soon as unlimited fixes are ready.`}
              </p>
              <RateLimitWaitlistForm tool="RESUME_ROASTER" />
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <Card className="flex-col border-border shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl text-foreground">{t`Resume Roaster`}</CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {t`PDFs only. Keep it under 5 MB. The roast streams in live as it's generated.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <button
                  type="button"
                  className={cn(
                    "flex min-h-[260px] w-full flex-col items-center justify-center gap-4 rounded-3xl border border-dashed px-6 py-10 text-center transition",
                    isDragging
                      ? "border-fuchsia-300 bg-fuchsia-400/10"
                      : "border-border bg-muted/40 hover:border-fuchsia-300/40 hover:bg-fuchsia-400/5",
                  )}
                  onClick={() => {
                    inputRef.current?.click();
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => {
                    setIsDragging(false);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    onFileSelected(event.dataTransfer.files.item(0));
                  }}
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-fuchsia-400/10 text-fuchsia-700 dark:text-fuchsia-300">
                    <UploadSimpleIcon size={28} />
                  </div>
                  <div className="space-y-2">
                    <p className="text-lg font-medium text-foreground">
                      {t`Drop your resume PDF here or click to browse`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t`We'll extract the text, roast the weak spots, and end with one honest bright spot.`}
                    </p>
                  </div>

                  {selectedFile ? (
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-4 py-2 text-sm text-foreground">
                      <FilePdfIcon size={18} className="text-fuchsia-700 dark:text-fuchsia-200" />
                      <span className="max-w-[220px] truncate">{selectedFile.name}</span>
                    </div>
                  ) : null}
                </button>

                <input
                  ref={inputRef}
                  hidden
                  accept=".pdf,application/pdf"
                  type="file"
                  onChange={(event) => {
                    onFileSelected(event.target.files?.item(0) ?? null);
                    event.target.value = "";
                  }}
                />

                {error ? <p className="text-sm text-error">{error}</p> : null}

                <Button
                  className="h-11 w-full"
                  disabled={!selectedFile || isStreaming}
                  onClick={onSubmit}
                >
                  {isStreaming ? (
                    <>
                      <CircleNotchIcon className="mr-2 animate-spin" size={18} />
                      {t`Roasting in progress...`}
                    </>
                  ) : (
                    t`Roast my resume`
                  )}
                </Button>

                <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 p-4 text-sm text-amber-900 dark:text-amber-50">
                  {t`Daily limit: 5 roasts per IP. We stream the roast live so you don't stare at a blank screen.`}
                </div>
              </CardContent>
            </Card>

            <Card className="flex-col border-border shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl text-foreground">
                  {isComplete ? t`Your roast is ready` : t`Live roast feed`}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {t`The roast lands first, then a separate encouragement section when the stream is done.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {roastText ? (
                  <>
                    <div className="rounded-3xl border border-border bg-muted p-5">
                      <div className="mb-3 text-xs font-medium text-fuchsia-700 dark:text-fuchsia-200">
                        {isComplete ? t`The Roast` : t`Streaming roast`}
                      </div>
                      <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-7 text-foreground">
                        {isComplete ? splitContent.roast : roastText}
                        {isStreaming ? (
                          <span className="animate-pulse text-fuchsia-700 dark:text-fuchsia-200">
                            |
                          </span>
                        ) : null}
                      </pre>
                    </div>

                    {isComplete && splitContent.encouragement ? (
                      <div className="rounded-3xl border border-success/30 bg-success-soft p-5">
                        <div className="mb-3 text-xs font-medium text-success-strong">
                          {t`But here's the thing...`}
                        </div>
                        <p className="whitespace-pre-wrap break-words text-sm leading-7 text-success-strong">
                          {splitContent.encouragement}
                        </p>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="rounded-3xl border border-border bg-muted/40 p-6 text-sm leading-7 text-muted-foreground">
                    {t`Upload a real PDF resume and the stream will start here. If the file is a scan with no selectable text, we'll tell you before the roast begins.`}
                  </div>
                )}

                {isComplete ? (
                  <>
                    <ToolShareButtons
                      message={shareMessage}
                      title={t`Apply Edge Resume Roaster`}
                      url={toolUrl}
                    />

                    <Button asChild className="w-full">
                      <Link
                        to="/auth/register"
                        onClick={() => {
                          recordToolCtaClick("RESUME_ROASTER", "results");
                        }}
                      >
                        {t`Now fix it with Apply Edge`}
                        <ArrowRightIcon className="ml-2" size={18} />
                      </Link>
                    </Button>
                  </>
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
